import { User } from "@/types";
import { GoogleSignin } from "@react-native-google-signin/google-signin";
import { Platform } from "react-native";
import * as Linking from "expo-linking";
import * as WebBrowser from "expo-web-browser";
import { ApiResponse, POST } from "./index";
import FBTrackingService from "./fbTrackingService";

/** Web: `handoff: "redirect"` means the tab navigated to Google; completion uses `?gmail_link=`. */
export type GoogleAuthHandleResult =
  | ApiResponse<{ success: boolean }>
  | { success: boolean; handoff?: "redirect" };

/** Web-only: query param set by server after full-page Google OAuth. Values: success | error | cancel */
export const GMAIL_LINK_QUERY = "gmail_link" as const;

/**
 * True for phone/tablet mobile browsers (Expo web). Full-page Google OAuth avoids popup blockers.
 */
export function isMobileWebBrowser(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  if (
    /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  ) {
    return true;
  }
  if (
    typeof window !== "undefined" &&
    navigator.maxTouchPoints > 1 &&
    window.innerWidth < 900
  ) {
    return true;
  }
  return false;
}

GoogleSignin.configure({
  webClientId:
    "198717928667-5l6r2gjd4avr2rfmrbm7eep2ub2vunl7.apps.googleusercontent.com",
  scopes: [
    "https://www.googleapis.com/auth/gmail.readonly",
    "https://www.googleapis.com/auth/gmail.send",
    "https://www.googleapis.com/auth/userinfo.email",
  ], // what API you want to access on behalf of the user, default is email and profile
  offlineAccess: true, // if you want to access Google API on behalf of the user FROM YOUR SERVER
  iosClientId:
    "198717928667-e5i5ghepa286qepr887dtk46u4pcm1ns.apps.googleusercontent.com",
});

// Sign in request interface
export interface SignInRequest {
  email: string;
  password: string;
}

// Sign in response interface (adjust based on your API response structure)
export interface SignInResponse {
  sessionId: string;
}

// Authentication service functions
const AuthServices = {
  /**
   * Sign in user with email and password
   * @param credentials - User email and password
   * @returns Promise<ApiResponse<SignInResponse>>
   */
  signIn: async (
    credentials: SignInRequest,
  ): Promise<ApiResponse<SignInResponse>> => {
    return await POST<SignInResponse>("/auth/sign-in", credentials);
  },
  getUserProfile: async () => {
    return POST<User>("/user-details");
  },
  deleteAccount: async () => {
    return POST("/user/delete-account");
  },
  initUser: async (email: string) => {
    return POST<{ userId: string }>("/init-user", { email });
  },
  getGoogleAuthUrl: async (userId?: string, returnUrl?: string) => {
    return POST<{ authUrl: string }>("/auth/google", { userId, returnUrl });
  },
  handleGoogleAuth: async (userId?: string): Promise<GoogleAuthHandleResult> => {
    // Handle web auth
    if (Platform.OS === "web") {
      return new Promise<GoogleAuthHandleResult>(async (resolve) => {
        let intervalCheck: ReturnType<typeof setInterval> | undefined;
        const handleAuthMessage = (event: MessageEvent) => {
          const data = event.data;
          if (data.type === "auth_success") {
            cleanup();
            resolve({ success: true });
          } else if (data.type === "auth_error") {
            cleanup();
            resolve({ success: false });
          }
        };
        const cleanup = () => {
          window.removeEventListener("message", handleAuthMessage);
          if (intervalCheck !== undefined) {
            clearInterval(intervalCheck);
          }
        };
        const mobileWeb =
          typeof window !== "undefined" && isMobileWebBrowser();
        const returnUrl =
          mobileWeb && typeof window !== "undefined"
            ? `${window.location.origin}${window.location.pathname}${window.location.search}`
            : undefined;
        const authUrlRes = await AuthServices.getGoogleAuthUrl(
          userId,
          returnUrl,
        );
        if (!authUrlRes.success) {
          resolve({ success: false });
          return;
        }
        const { authUrl } = authUrlRes.data;
        if (mobileWeb && returnUrl) {
          window.location.assign(authUrl);
          resolve({ success: true, handoff: "redirect" });
          return;
        }
        const left = (window.innerWidth - 600) / 2;
        const top = (window.innerHeight - 700) / 2;
        const authPopup = window.open(
          authUrl,
          `Google Auth`,
          `width=${600},height=${700},top=${top},left=${left}`,
        );
        window.addEventListener("message", handleAuthMessage);
        intervalCheck = setInterval(() => {
          if (!authPopup || authPopup?.closed) {
            cleanup();
            resolve({ success: false });
          }
        }, 500);
      });
    }
    // Handle mobile auth
    const response = await GoogleSignin.signIn();
    if (response.data?.user?.email) {
      FBTrackingService.trackLead({
        type: "link_gmail",
        email: response.data?.user?.email,
      });
    }
    setTimeout(() => {
      GoogleSignin.signOut();
    }, 1000);
    const code = response.data?.serverAuthCode;
    if (!code) {
      return { success: false };
    }
    return POST<{ success: boolean }>("/auth/google/code", {
      code,
      userId,
    });
  },
  createUser: async ({
    email,
    password,
    referalId,
  }: {
    email: string;
    password: string;
    referalId?: string;
  }) => {
    const signupPlatform =
      Platform.OS === "web" ? "web" : Platform.OS === "ios" ? "ios" : "android";

    return POST<{ sessionId: string }>("/user/create", {
      email,
      password,
      referalId,
      signupPlatform,
    });
  },
  forgotPassword: async (email: string) => {
    return POST("/auth/forgot-password", { email });
  },
  getOutlookAuthUrl: async (userId?: string) => {
    return POST<{ authUrl: string }>("/auth/outlook", { userId });
  },
  handleOutlookAuth: async (userId?: string) => {
    // Handle web auth
    if (Platform.OS === "web") {
      return new Promise<{ success: boolean }>(async (resolve) => {
        let intervalCheck: ReturnType<typeof setInterval>;
        const handleAuthMessage = (event: MessageEvent) => {
          const data = event.data;
          if (data.type === "auth_success" && data.authType === "outlook") {
            cleanup();
            resolve({ success: true });
          } else if (data.type === "auth_error") {
            cleanup();
            resolve({ success: false });
          }
        };
        const cleanup = () => {
          window.removeEventListener("message", handleAuthMessage);
          clearInterval(intervalCheck);
        };
        const authUrlRes = await AuthServices.getOutlookAuthUrl(userId);
        if (authUrlRes.success) {
          const { authUrl } = authUrlRes.data;
          const left = (window.innerWidth - 600) / 2;
          const top = (window.innerHeight - 700) / 2;
          const authPopup = window.open(
            authUrl,
            `Outlook Auth`,
            `width=${600},height=${700},top=${top},left=${left}`,
          );
          window.addEventListener("message", handleAuthMessage);
          intervalCheck = setInterval(() => {
            if (!authPopup || authPopup?.closed) {
              cleanup();
              resolve({ success: false });
            }
          }, 500);
        } else {
          resolve({ success: false });
        }
      });
    }
    // Handle mobile auth using WebBrowser
    try {
      const authUrlRes = await AuthServices.getOutlookAuthUrl(userId);
      if (!authUrlRes.success) {
        return { success: false };
      }
      const { authUrl } = authUrlRes.data;
      const result = await WebBrowser.openAuthSessionAsync(
        authUrl,
        Linking.createURL("/auth/outlook/callback"),
      );
      if (result.type === "success" && result.url) {
        // The callback URL contains success info
        const url = new URL(result.url);
        const success = url.searchParams.get("success") === "true";
        return { success };
      }
      return { success: false };
    } catch (error) {
      console.error("Outlook auth error:", error);
      return { success: false };
    }
  },
  getYahooAuthUrl: async (userId?: string) => {
    return POST<{ authUrl: string }>("/auth/yahoo", { userId });
  },
  handleYahooAuth: async (userId?: string) => {
    // Handle web auth
    if (Platform.OS === "web") {
      return new Promise<{ success: boolean }>(async (resolve) => {
        let intervalCheck: ReturnType<typeof setInterval>;
        const handleAuthMessage = (event: MessageEvent) => {
          const data = event.data;
          if (data.type === "auth_success" && data.authType === "yahoo") {
            cleanup();
            resolve({ success: true });
          } else if (data.type === "auth_error") {
            cleanup();
            resolve({ success: false });
          }
        };
        const cleanup = () => {
          window.removeEventListener("message", handleAuthMessage);
          clearInterval(intervalCheck);
        };
        const authUrlRes = await AuthServices.getYahooAuthUrl(userId);
        if (authUrlRes.success) {
          const { authUrl } = authUrlRes.data;
          const left = (window.innerWidth - 600) / 2;
          const top = (window.innerHeight - 700) / 2;
          const authPopup = window.open(
            authUrl,
            `Yahoo Auth`,
            `width=${600},height=${700},top=${top},left=${left}`,
          );
          window.addEventListener("message", handleAuthMessage);
          intervalCheck = setInterval(() => {
            if (!authPopup || authPopup?.closed) {
              cleanup();
              resolve({ success: false });
            }
          }, 500);
        } else {
          resolve({ success: false });
        }
      });
    }
    // Handle mobile auth using WebBrowser
    try {
      const authUrlRes = await AuthServices.getYahooAuthUrl(userId);
      if (!authUrlRes.success) {
        return { success: false };
      }
      const { authUrl } = authUrlRes.data;
      const result = await WebBrowser.openAuthSessionAsync(
        authUrl,
        Linking.createURL("/auth/yahoo/callback"),
      );
      if (result.type === "success" && result.url) {
        // The callback URL contains success info
        const url = new URL(result.url);
        const success = url.searchParams.get("success") === "true";
        return { success };
      }
      return { success: false };
    } catch (error) {
      console.error("Yahoo auth error:", error);
      return { success: false };
    }
  },
};

export default AuthServices;
